import { AppDispatch } from "app/store";
import {
  selectAddress,
  selectBuildingName,
  selectCity,
  selectCompany,
  selectCompanyFurigana,
  selectCompanyTel,
  selectCountry,
  selectDdepartment,
  selectFamilyName,
  selectFamilyNameFurigana,
  selectFirstName,
  selectFirstNameFurigana,
  selectInputValidationErrors,
  selectJobType,
  selectOffice,
  selectPosition,
  selectPostalCode,
  selectMarket_Status,
  selectPostalError,
  selectState,
  changeAddress,
  changeBuildingName,
  changeCity,
  changeCompany,
  changeCompanyFurigana,
  changeCompanyTel,
  changeDepartment,
  changeFamilyNameFurigana,
  changeFirstNameFurigana,
  changeJobType,
  changeOffice,
  changePosition,
  changePostalCode,
  changeState,
  changeMarket_Status,
  cleanupByCountyChange,
  changeCountry,
  changeFamilyName,
  changeFirstName,
  postalCodeAsync,
  updateAsync,
} from "features/profile";
import { ErrorChacekResults } from "features/profile/types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import { inputtingPattern } from "common/validateTelephoneNumber";
import { focusingScroll } from "common/focusingScroll";
import {
  NAME_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  PROFILE_COLUMN_LENGTH,
  TEL_MAX_LENGTH,
} from "../../common/constants";
import { COUNTRY_LIST, JOB_TYPE_LIST, PREFECTURE_LIST } from "./constants";

interface Param {
  scrollOffset: number;
}

const ProfileInputJapan: React.FC<Param> = ({
  scrollOffset,
}: Param): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const country = useSelector(selectCountry);
  const familyName = useSelector(selectFamilyName);
  const firstName = useSelector(selectFirstName);
  const familyNameFurigana = useSelector(selectFamilyNameFurigana);
  const firstNameFurigana = useSelector(selectFirstNameFurigana);
  const company = useSelector(selectCompany);
  const companyFurigana = useSelector(selectCompanyFurigana);
  const office = useSelector(selectOffice);
  const department = useSelector(selectDdepartment);
  const position = useSelector(selectPosition);
  const jobType = useSelector(selectJobType);
  const companyTel = useSelector(selectCompanyTel);
  const postalCode = useSelector(selectPostalCode);
  const state = useSelector(selectState);
  const city = useSelector(selectCity);
  const address = useSelector(selectAddress);
  const buildingName = useSelector(selectBuildingName);
  const market_status = useSelector(selectMarket_Status);
  const errors = useSelector(selectInputValidationErrors);
  const postalError = useSelector(selectPostalError);
  const { t } = useTranslation();
  const [isFocusing, setForcusing] = useState(false);

  const countryRef = useRef<HTMLElement>(null);
  const fullNameRef = useRef<HTMLElement>(null);
  const fullNameFuriganaRef = useRef<HTMLElement>(null);
  const companyRef = useRef<HTMLElement>(null);
  const companyFuriganaRef = useRef<HTMLElement>(null);
  const companyTelRef = useRef<HTMLElement>(null);
  const postalCodeRef = useRef<HTMLElement>(null);
  const prefectureRef = useRef<HTMLElement>(null);
  const cityRef = useRef<HTMLElement>(null);

  const [error, setError] = useState<ErrorChacekResults>({
    hasErrorEmptyCountry: false,
    hasErrorEmptyName: false,
    hasErrorEmptyCompany: false,
    hasErrorEmptyPostalCode: false,
    hasErrorEmptyState: false,
    hasErrorEmptyCity: false,
    hasErrorIncorrectNameFurigana: false,
    hasErrorIncorrectCompanyFurigana: false,
    hasErrorIncorrectPostalCode: false,
    hasErrorIncorrectTelephoneNumber: false,
  });

  const onSubmit = useCallback(() => {
    setError(errors);
    if (
      errors.hasErrorEmptyCountry ||
      errors.hasErrorEmptyName ||
      errors.hasErrorEmptyCompany ||
      errors.hasErrorEmptyPostalCode ||
      errors.hasErrorEmptyState ||
      errors.hasErrorEmptyCity ||
      errors.hasErrorIncorrectNameFurigana ||
      errors.hasErrorIncorrectCompanyFurigana ||
      errors.hasErrorIncorrectPostalCode ||
      errors.hasErrorIncorrectTelephoneNumber
    ) {
      setForcusing(true);
      return;
    }
    dispatch(updateAsync());
  }, [dispatch, errors]);

  useEffect(() => {
    if (!isFocusing) {
      return;
    }

    let element: HTMLElement | null;
    if (errors.hasErrorEmptyCountry) {
      element = countryRef.current;
    } else if (errors.hasErrorEmptyName) {
      element = fullNameRef.current;
    } else if (errors.hasErrorIncorrectNameFurigana) {
      element = fullNameFuriganaRef.current;
    } else if (errors.hasErrorEmptyCompany) {
      element = companyRef.current;
    } else if (errors.hasErrorIncorrectCompanyFurigana) {
      element = companyFuriganaRef.current;
    } else if (errors.hasErrorIncorrectTelephoneNumber) {
      element = companyTelRef.current;
    } else if (
      errors.hasErrorEmptyPostalCode ||
      errors.hasErrorIncorrectPostalCode
    ) {
      element = postalCodeRef.current;
    } else if (errors.hasErrorEmptyState) {
      element = prefectureRef.current;
    } else if (errors.hasErrorEmptyCity) {
      element = cityRef.current;
    } else {
      element = null;
    }
    if (element != null) {
      focusingScroll(element, scrollOffset);
    }
    setForcusing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocusing]);

  return (
    <Section>
      <Title>{t("profilePage.label.profile")}</Title>
      <form>
        <FormList>
          <FormDt ref={countryRef}>
            {t("profilePage.label.country")}
            <Required>{t("profilePage.label.required")}</Required>
          </FormDt>
          <FormDd>
            <FormSelect
              value={country}
              isError={error.hasErrorEmptyCountry}
              onChange={(event) => {
                if (
                  event.target.value !== "" &&
                  event.target.value !== country
                ) {
                  dispatch(cleanupByCountyChange());
                }
                dispatch(changeCountry({ country: event.target.value }));
              }}
            >
              <Option value="">{t("profilePage.label.pullDownSelect")}</Option>
              {COUNTRY_LIST.map((x) => (
                <Option key={x.value} disabled={x.disabled} value={x.value}>
                  {x.disabled || <>&nbsp;&nbsp;</>}
                  {x.label}
                </Option>
              ))}
            </FormSelect>
            {error.hasErrorEmptyCountry && (
              <ErrorComment>
                {t("profilePage.message.requiredEmptyError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt ref={fullNameRef}>
            {t("profilePage.label.fullName")}
            <Required>{t("profilePage.label.required")}</Required>
          </FormDt>
          <FormDd>
            {t("profilePage.label.familyName")}&emsp;：
            <FormInput
              value={familyName}
              isError={error.hasErrorEmptyName}
              type="text"
              size={15}
              maxLength={NAME_MAX_LENGTH}
              onChange={(event) =>
                dispatch(changeFamilyName({ familyName: event.target.value }))
              }
              placeholder={t("profilePage.label.familyName_pl")}
            />
            &emsp;{t("profilePage.label.givenName")}&emsp;：
            <FormInput
              value={firstName}
              isError={error.hasErrorEmptyName}
              type="text"
              size={15}
              maxLength={NAME_MAX_LENGTH}
              onChange={(event) =>
                dispatch(changeFirstName({ firstName: event.target.value }))
              }
              placeholder={t("profilePage.label.givenName_pl")}
            />
            {error.hasErrorEmptyName && (
              <ErrorComment>
                {t("profilePage.message.requiredEmptyError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt ref={fullNameFuriganaRef}>
            {t("profilePage.label.fullNameFurigana")}
            <FormComment>
              {t("profilePage.label.nameFuriganaFormat")}
            </FormComment>
          </FormDt>
          <FormDd>
            {t("profilePage.label.familyNameFurigana")}：
            <FormInput
              value={familyNameFurigana}
              isError={error.hasErrorIncorrectNameFurigana}
              type="text"
              size={15}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(
                  changeFamilyNameFurigana({
                    familyNameFurigana: event.target.value,
                  })
                )
              }
              placeholder={t("profilePage.label.familyNameFurigana_pl")}
            />
            &emsp;
            {t("profilePage.label.givenNameFurigana")}：
            <FormInput
              value={firstNameFurigana}
              isError={error.hasErrorIncorrectNameFurigana}
              type="text"
              size={15}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(
                  changeFirstNameFurigana({
                    firstNameFurigana: event.target.value,
                  })
                )
              }
              placeholder={t("profilePage.label.givenNameFurigana_pl")}
            />
            {error.hasErrorIncorrectNameFurigana && (
              <ErrorComment>
                {t("profilePage.message.invalidFuriganaFormatError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt ref={companyRef}>
            {t("profilePage.label.company")}
            <Required>{t("profilePage.label.required")}</Required>
          </FormDt>
          <FormDd>
            <FormInput
              value={company}
              isError={error.hasErrorEmptyCompany}
              type="text"
              size={40}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(changeCompany({ company: event.target.value }))
              }
              placeholder={t("profilePage.label.company_pl")}
            />
            <FormComment>{t("profilePage.text.companyComment")}</FormComment>
            {error.hasErrorEmptyCompany && (
              <ErrorComment>
                {t("profilePage.message.requiredEmptyError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt ref={companyFuriganaRef}>
            {t("profilePage.label.companyFurigana")}
            <FormComment>
              {t("profilePage.label.nameFuriganaFormat")}
            </FormComment>
          </FormDt>
          <FormDd>
            <FormInput
              value={companyFurigana}
              isError={error.hasErrorIncorrectCompanyFurigana}
              type="text"
              size={40}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(
                  changeCompanyFurigana({ companyFurigana: event.target.value })
                )
              }
              placeholder={t("profilePage.label.companyFurigana_pl")}
            />
            <FormComment>
              {t("profilePage.text.companyFuriganaComment")}
            </FormComment>
            {error.hasErrorIncorrectCompanyFurigana && (
              <ErrorComment>
                {t("profilePage.message.invalidFuriganaFormatError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt>{t("profilePage.label.office")}</FormDt>
          <FormDd>
            <FormInput
              value={office}
              type="text"
              size={40}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(changeOffice({ office: event.target.value }))
              }
              placeholder={t("profilePage.label.office_pl")}
            />
          </FormDd>
          <FormDt>{t("profilePage.label.department")}</FormDt>
          <FormDd>
            <FormInput
              value={department}
              type="text"
              size={40}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(changeDepartment({ department: event.target.value }))
              }
              placeholder={t("profilePage.label.department_pl")}
            />
            <FormComment>{t("profilePage.text.departmentComment")}</FormComment>
          </FormDd>
          <FormDt>{t("profilePage.label.position")}</FormDt>
          <FormDd>
            <FormInput
              value={position}
              type="text"
              size={40}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(changePosition({ position: event.target.value }))
              }
              placeholder={t("profilePage.label.position_pl")}
            />
          </FormDd>
          <FormDt>{t("profilePage.label.jobType")}</FormDt>
          <FormDd>
            <FormSelect
              value={jobType}
              onChange={(event) =>
                dispatch(changeJobType({ jobType: event.target.value }))
              }
            >
              <Option value="">{t("profilePage.label.pullDownSelect")}</Option>
              {JOB_TYPE_LIST.map((x) => (
                <Option key={x.value} value={x.value}>
                  {t(x.label)}
                </Option>
              ))}
            </FormSelect>
          </FormDd>
          <FormDt ref={companyTelRef}>
            {t("profilePage.label.companyTel")}
            <FormComment>{t("profilePage.label.companyTelFormat")}</FormComment>
          </FormDt>
          <FormDd>
            <FormInput
              value={companyTel}
              isError={error.hasErrorIncorrectTelephoneNumber}
              type="tel"
              size={40}
              maxLength={TEL_MAX_LENGTH}
              marginRignt="0.9rem"
              onChange={(event) => {
                if (event.target.value.match(inputtingPattern)?.length !== 1) {
                  event.target.value = companyTel;
                }
                dispatch(changeCompanyTel({ companyTel: event.target.value }));
              }}
            />
            {error.hasErrorIncorrectTelephoneNumber && (
              <ErrorComment>
                {t("signupPage.message.invalidTelephoneNumberFormatError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt ref={postalCodeRef}>
            {t("profilePage.label.postalCode")}
            <Required>{t("profilePage.label.required")}</Required>
            <FormComment>{t("profilePage.label.postalCodeFormat")}</FormComment>
          </FormDt>
          <FormDd>
            <FormInput
              value={postalCode}
              isError={
                error.hasErrorEmptyPostalCode ||
                error.hasErrorIncorrectPostalCode
              }
              type="text"
              size={20}
              maxLength={POSTAL_CODE_MAX_LENGTH}
              onChange={(event) =>
                dispatch(changePostalCode({ postalCode: event.target.value }))
              }
            />
            &emsp;
            <FormButton
              type="button"
              value={t("profilePage.label.postalCodeButton")}
              onClick={() => {
                dispatch(postalCodeAsync());
              }}
            />
            {error.hasErrorEmptyPostalCode && (
              <ErrorComment>
                {t("profilePage.message.requiredEmptyError")}
              </ErrorComment>
            )}
            {postalError !== "BadRequest" &&
              error.hasErrorIncorrectPostalCode && (
                <ErrorComment>
                  {t("profilePage.message.invalidPostalCodeFormatError")}
                </ErrorComment>
              )}
            {postalError === "BadRequest" && (
              <ErrorComment>
                {t("profilePage.message.invalidPostalCodeFormatError")}
              </ErrorComment>
            )}
            {postalError === "Error" && (
              <ErrorComment>
                {t("profilePage.message.faildGetPostalCodeError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt ref={prefectureRef}>
            {t("profilePage.label.prefectures")}
            <Required>{t("profilePage.label.required")}</Required>
          </FormDt>
          <FormDd>
            <FormSelect
              value={state}
              isError={error.hasErrorEmptyState}
              onChange={(event) =>
                dispatch(changeState({ state: event.target.value }))
              }
            >
              <Option value="">{t("profilePage.label.pullDownSelect")}</Option>
              {PREFECTURE_LIST.map((x) => (
                <Option key={x.value} value={x.value}>
                  {x.label}
                </Option>
              ))}
            </FormSelect>
            {error.hasErrorEmptyState && (
              <ErrorComment>
                {t("profilePage.message.requiredEmptyError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt ref={cityRef}>
            {t("profilePage.label.city")}
            <Required>{t("profilePage.label.required")}</Required>
          </FormDt>
          <FormDd>
            <FormInput
              value={city}
              isError={error.hasErrorEmptyCity}
              type="text"
              size={60}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(changeCity({ city: event.target.value }))
              }
            />
            {error.hasErrorEmptyCity && (
              <ErrorComment>
                {t("profilePage.message.requiredEmptyError")}
              </ErrorComment>
            )}
          </FormDd>
          <FormDt>{t("profilePage.label.address")}</FormDt>
          <FormDd>
            <FormInput
              value={address}
              type="text"
              size={60}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(changeAddress({ address: event.target.value }))
              }
            />
          </FormDd>
          <FormDt>{t("profilePage.label.building")}</FormDt>
          <FormDd>
            <FormInput
              value={buildingName}
              type="text"
              size={60}
              maxLength={PROFILE_COLUMN_LENGTH}
              onChange={(event) =>
                dispatch(
                  changeBuildingName({ buildingName: event.target.value })
                )
              }
            />
          </FormDd>
          <RadioButtonArea marginBottom="2rem">
            <p>{t("profilePage.label.market_status")}</p>
            <div>
              <label>
                <FormRadio
                  marginLeft="2rem"
                  type="radio"
                  value="yes"
                  name="market_state"
                  checked={market_status === true}
                  onChange={() =>
                    dispatch(changeMarket_Status({ market_status: true }))
                  }
                />
                {t("profilePage.label.market_status_yes")}
              </label>
              <label>
                <FormRadio
                  marginLeft="4rem"
                  type="radio"
                  value="no"
                  name="market_state"
                  checked={market_status === false}
                  onChange={() =>
                    dispatch(changeMarket_Status({ market_status: false }))
                  }
                />
                {t("profilePage.label.market_status_no")}
              </label>
            </div>
          </RadioButtonArea>

          <SubmitArea>
            <SubmitButton
              type="button"
              name=""
              value={t("profilePage.label.saveButton")}
              onClick={onSubmit}
            />
          </SubmitArea>
        </FormList>
      </form>
    </Section>
  );
};

export default ProfileInputJapan;

const Title = styled.h2`
  margin-bottom: 3rem;
`;
const FormDt = styled.dt<{ marginBottom?: string }>`
  margin-bottom: ${(props) => props.marginBottom || "0"};

  width: 15rem;
  padding: 1.3rem 0.5rem;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
`;
const FormDd = styled.dd<{ marginBottom?: string }>`
  margin-bottom: ${(props) => props.marginBottom || "0"};
  width: calc(100% - 17rem);
  padding: 1.3rem 0.5rem;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
`;

const FormSelect = styled.select<{ isError?: boolean }>`
  padding: 0.5rem 0.8rem;
  border: 1px #999999 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  :focus {
    outline: 0;
    background: #fafafa;
  }
  ${({ isError }) =>
    isError &&
    css`
      background: rgba(229, 0, 0, 0.08);
      :focus {
        background: rgba(229, 0, 0, 0.08);
      }
    `}
`;
const ErrorComment = styled.span`
  margin-top: 0.2rem;
  display: block;
  color: #e50000;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;
const FormComment = styled.span<{ marginTop?: string }>`
  margin-top: ${(props) => props.marginTop || "0"};
  display: block;
  color: #999999;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;
const Option = styled.option`
  :disabled {
    background: #e6e6e6;
  }
`;
const Required = styled.p`
  display: inline-block;
  color: #e50000;
  float: right;
  padding-top: 0.3rem;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;

const Section = styled.section`
  display: block;
`;

const FormList = styled.dl`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 3rem;
`;

const FormInput = styled.input<{
  marginRignt?: string;
  marginTop?: string;
  isError?: boolean;
}>`
  margin-right: ${(props) => props.marginRignt || "0"};
  margin-top: ${(props) => props.marginTop || "0"};
  padding: 0.5rem 0.8rem;
  border: 1px #999999 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  :focus {
    outline: 0;
    background: #fafafa;
  }

  ${({ isError }) =>
    isError &&
    css`
      background: rgba(229, 0, 0, 0.08);
      :focus {
        background: rgba(229, 0, 0, 0.08);
      }
    `}
`;

const FormRadio = styled(FormInput)<{
  marginLeft?: string;
}>`
  margin-right: 10px;
  margin-left: ${(props) => props.marginLeft || "0"};
  position: relative;
  top: 2px;
`;

const TelSpan = styled.span`
  margin-right: 0.9rem;
`;

const FormButton = styled(FormInput)`
  padding: 0.5rem 0.8rem;
  border: 1px #999999 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  background: #f0f0f0;
  cursor: pointer;
  :hover {
    background: #e6e6e6;
  }
`;

const RadioButtonArea = styled(FormDd)`
  margin-bottom: ${(props) => props.marginBottom || "0"};
  padding: 1.3rem 0 1.3rem 16.5rem;
  width: 36rem;
`;

const SubmitArea = styled(FormDd)`
  padding: 1.3rem 0 1.3rem 16.5rem;
`;

const SubmitButton = styled.input`
  cursor: pointer;
  opacity: 1;
  color: #ffffff;
  background: #005eb8;
  border: 1px #005eb8 solid;
  pointer-events: auto;
  min-width: 15rem;
  padding: 1.23rem 2rem;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  transition: all 0.3s ease-out;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
`;
